/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { BoardsApiConfiguration } from '../boards-api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { ActionDetails } from '../models/action-details';
import { ActionSummary } from '../models/action-summary';
import { AddActionRequest } from '../models/add-action-request';
import { CreateOrUpdateActionRequest } from '../models/create-or-update-action-request';
import { UpdateActionRequest } from '../models/update-action-request';

@Injectable({
  providedIn: 'root',
})
export class ActionsService extends BaseService {
  constructor(config: BoardsApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation getActions
   */
  static readonly GetActionsPath = '/Actions';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getActions()` instead.
   *
   * This method doesn't expect any request body.
   */
  getActions$Response(params: {
    /**
     * Organisation the user is acting under for this operation
     */
    organisationId: string;

    /**
     * Id of the board to which the action belongs to
     */
    boardId: string;
    context?: HttpContext;
  }): Observable<
    StrictHttpResponse<{
      [key: string]: ActionSummary;
    }>
  > {
    const rb = new RequestBuilder(this.rootUrl, ActionsService.GetActionsPath, 'get');
    if (params) {
      rb.query('organisationId', params.organisationId, {});
      rb.query('boardId', params.boardId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<{
            [key: string]: ActionSummary;
          }>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getActions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getActions(params: {
    /**
     * Organisation the user is acting under for this operation
     */
    organisationId: string;

    /**
     * Id of the board to which the action belongs to
     */
    boardId: string;
    context?: HttpContext;
  }): Observable<{
    [key: string]: ActionSummary;
  }> {
    return this.getActions$Response(params).pipe(
      map(
        (
          r: StrictHttpResponse<{
            [key: string]: ActionSummary;
          }>
        ) =>
          r.body as {
            [key: string]: ActionSummary;
          }
      )
    );
  }

  /**
   * Path part for operation addAction
   */
  static readonly AddActionPath = '/Actions';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `addAction()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addAction$Response(params: {
    /**
     * Organisation the user is acting under for this operation
     */
    organisationId: string;

    /**
     * Id of the board to which the action should be added
     */
    boardId: string;

    /**
     * Requested return type
     */
    returnType: string;
    context?: HttpContext;
    body: AddActionRequest;
  }): Observable<StrictHttpResponse<{}>> {
    const rb = new RequestBuilder(this.rootUrl, ActionsService.AddActionPath, 'post');
    if (params) {
      rb.query('organisationId', params.organisationId, {});
      rb.query('boardId', params.boardId, {});
      rb.query('returnType', params.returnType, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<{}>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `addAction$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addAction(params: {
    /**
     * Organisation the user is acting under for this operation
     */
    organisationId: string;

    /**
     * Id of the board to which the action should be added
     */
    boardId: string;

    /**
     * Requested return type
     */
    returnType: string;
    context?: HttpContext;
    body: AddActionRequest;
  }): Observable<{}> {
    return this.addAction$Response(params).pipe(map((r: StrictHttpResponse<{}>) => r.body as {}));
  }

  /**
   * Path part for operation createOrUpdateAction
   */
  static readonly CreateOrUpdateActionPath = '/Boards/{boardGuid}/Actions/{actionGuid}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createOrUpdateAction()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createOrUpdateAction$Response(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;

    /**
     * Id of the board that the Action belongs to.
     */
    boardGuid: string;

    /**
     * Id of the action to create or update.
     */
    actionGuid: string;
    context?: HttpContext;
    body: CreateOrUpdateActionRequest;
  }): Observable<StrictHttpResponse<ActionDetails>> {
    const rb = new RequestBuilder(this.rootUrl, ActionsService.CreateOrUpdateActionPath, 'put');
    if (params) {
      rb.query('organisationId', params.organisationId, {});
      rb.path('boardGuid', params.boardGuid, {});
      rb.path('actionGuid', params.actionGuid, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ActionDetails>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createOrUpdateAction$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createOrUpdateAction(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;

    /**
     * Id of the board that the Action belongs to.
     */
    boardGuid: string;

    /**
     * Id of the action to create or update.
     */
    actionGuid: string;
    context?: HttpContext;
    body: CreateOrUpdateActionRequest;
  }): Observable<ActionDetails> {
    return this.createOrUpdateAction$Response(params).pipe(
      map((r: StrictHttpResponse<ActionDetails>) => r.body as ActionDetails)
    );
  }

  /**
   * Path part for operation getAction
   */
  static readonly GetActionPath = '/Actions/{actionId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAction()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAction$Response(params: {
    /**
     * Id of the action to retrieve
     */
    actionId: string;

    /**
     * Organisation the user is acting under for this operation
     */
    organisationId: string;

    /**
     * Id of the board to which the action belongs to
     */
    boardId: string;
    context?: HttpContext;
  }): Observable<StrictHttpResponse<ActionDetails>> {
    const rb = new RequestBuilder(this.rootUrl, ActionsService.GetActionPath, 'get');
    if (params) {
      rb.path('actionId', params.actionId, {});
      rb.query('organisationId', params.organisationId, {});
      rb.query('boardId', params.boardId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ActionDetails>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getAction$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAction(params: {
    /**
     * Id of the action to retrieve
     */
    actionId: string;

    /**
     * Organisation the user is acting under for this operation
     */
    organisationId: string;

    /**
     * Id of the board to which the action belongs to
     */
    boardId: string;
    context?: HttpContext;
  }): Observable<ActionDetails> {
    return this.getAction$Response(params).pipe(map((r: StrictHttpResponse<ActionDetails>) => r.body as ActionDetails));
  }

  /**
   * Path part for operation deleteAction
   */
  static readonly DeleteActionPath = '/Actions/{actionId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteAction()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteAction$Response(params: {
    /**
     * Id of the action to delete
     */
    actionId: string;

    /**
     * Organisation the user is acting under for this operation
     */
    organisationId: string;

    /**
     * Id of the board to which the action belong to
     */
    boardId: string;

    /**
     * _etag for concurrency check
     */
    _etag: string;
    context?: HttpContext;
  }): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(this.rootUrl, ActionsService.DeleteActionPath, 'delete');
    if (params) {
      rb.path('actionId', params.actionId, {});
      rb.query('organisationId', params.organisationId, {});
      rb.query('boardId', params.boardId, {});
      rb.query('_etag', params['_etag'], {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteAction$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteAction(params: {
    /**
     * Id of the action to delete
     */
    actionId: string;

    /**
     * Organisation the user is acting under for this operation
     */
    organisationId: string;

    /**
     * Id of the board to which the action belong to
     */
    boardId: string;

    /**
     * _etag for concurrency check
     */
    _etag: string;
    context?: HttpContext;
  }): Observable<void> {
    return this.deleteAction$Response(params).pipe(map((r: StrictHttpResponse<void>) => r.body as void));
  }

  /**
   * Path part for operation updateAction
   */
  static readonly UpdateActionPath = '/Actions/{actionId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateAction()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAction$Response(params: {
    /**
     * Id of the action to retrieve
     */
    actionId: string;

    /**
     * Organisation the user is acting under for this operation
     */
    organisationId: string;

    /**
     * Id of the board to which the action belongs to
     */
    boardId: string;
    context?: HttpContext;
    body: UpdateActionRequest;
  }): Observable<StrictHttpResponse<ActionDetails>> {
    const rb = new RequestBuilder(this.rootUrl, ActionsService.UpdateActionPath, 'patch');
    if (params) {
      rb.path('actionId', params.actionId, {});
      rb.query('organisationId', params.organisationId, {});
      rb.query('boardId', params.boardId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ActionDetails>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateAction$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAction(params: {
    /**
     * Id of the action to retrieve
     */
    actionId: string;

    /**
     * Organisation the user is acting under for this operation
     */
    organisationId: string;

    /**
     * Id of the board to which the action belongs to
     */
    boardId: string;
    context?: HttpContext;
    body: UpdateActionRequest;
  }): Observable<ActionDetails> {
    return this.updateAction$Response(params).pipe(
      map((r: StrictHttpResponse<ActionDetails>) => r.body as ActionDetails)
    );
  }
}
